/**
 * `URLSessionWebSocketTask` over the browser's WebSocket (docs/ops-protocol.md,
 * "WebSockets"). Each call the task makes is a `websocket` request:
 *
 * - `open` connects; it answers `{ socket }` (the socket's id) once the socket
 *   is open, and fails like a fetch (`network: …`) when it cannot connect;
 * - `send` sends `string` (text) or `data` (base64 bytes) and answers `{}`;
 * - `receive` answers the next message, `{ string }` or `{ data }`. Messages
 *   that arrive with nobody receiving wait in a queue. Once the socket has
 *   closed and the queue is empty, it answers `{ closeCode, reason }`;
 * - `close` closes with `code` and `reason` and answers `{}`.
 */

import type { WebSocketRequestParams } from './protocol';

/** The part of `WebSocket` the requests use; tests substitute a fake. */
export interface WebSocketLike {
  binaryType: string;
  readonly readyState: number;
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onclose: ((event: { code: number; reason: string }) => void) | null;
  send(data: string | ArrayBufferLike | Uint8Array): void;
  close(code?: number, reason?: string): void;
}

export type WebSocketFactory = (url: string, protocols: string[]) => WebSocketLike;

export type WebSocketMessage = { string: string } | { data: string } | { closeCode: number; reason: string };

interface OpenSocket {
  socket: WebSocketLike;
  queue: WebSocketMessage[];
  waiting: ((message: WebSocketMessage) => void)[];
  closed?: { closeCode: number; reason: string };
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function bytes(base64Text: string): Uint8Array {
  const binary = atob(base64Text);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

/** The sockets one page has open, by id. */
export class WebSocketHost {
  private nextID = 1;
  private readonly sockets = new Map<number, OpenSocket>();

  constructor(private readonly create: WebSocketFactory) {}

  perform(params: WebSocketRequestParams): Promise<Record<string, unknown>> {
    switch (params.action) {
      case 'open':
        return this.open(params.url ?? '', params.protocols ?? []);
      case 'send':
        return Promise.resolve(this.send(params));
      case 'receive':
        return this.receive(params.socket ?? 0);
      case 'close':
        return Promise.resolve(this.close(params));
      default:
        return Promise.reject(new Error(`unsupported: websocket action ${String((params as { action: unknown }).action)}`));
    }
  }

  private open(url: string, protocols: string[]): Promise<Record<string, unknown>> {
    return new Promise((resolve, reject) => {
      let socket: WebSocketLike;
      try {
        socket = this.create(url, protocols);
      } catch (error) {
        reject(new Error(`network: ${error instanceof Error ? error.message : String(error)}`));
        return;
      }
      socket.binaryType = 'arraybuffer';
      const id = this.nextID;
      this.nextID += 1;
      const entry: OpenSocket = { socket, queue: [], waiting: [] };
      let opened = false;
      socket.onopen = () => {
        opened = true;
        this.sockets.set(id, entry);
        resolve({ socket: id });
      };
      socket.onmessage = (event) => {
        const data = event.data;
        const message: WebSocketMessage = typeof data === 'string'
          ? { string: data }
          : { data: base64(new Uint8Array(data as ArrayBuffer)) };
        this.deliver(entry, message);
      };
      socket.onerror = () => {
        if (!opened) reject(new Error(`network: cannot connect to ${url}`));
      };
      socket.onclose = (event) => {
        if (!opened) {
          reject(new Error(`network: the connection to ${url} closed (${event.code})`));
          return;
        }
        entry.closed = { closeCode: event.code, reason: event.reason };
        for (const wake of entry.waiting.splice(0)) wake(entry.closed);
      };
    });
  }

  private deliver(entry: OpenSocket, message: WebSocketMessage): void {
    const wake = entry.waiting.shift();
    if (wake) wake(message);
    else entry.queue.push(message);
  }

  private receive(id: number): Promise<Record<string, unknown>> {
    const entry = this.sockets.get(id);
    if (!entry) return Promise.reject(new Error(`network: no open socket ${id}`));
    const queued = entry.queue.shift();
    if (queued) return Promise.resolve(queued);
    if (entry.closed) return Promise.resolve(entry.closed);
    return new Promise((resolve) => entry.waiting.push(resolve));
  }

  private send(params: WebSocketRequestParams): Record<string, unknown> {
    const entry = this.sockets.get(params.socket ?? 0);
    if (!entry || entry.closed) throw new Error(`network: socket ${params.socket ?? 0} is not open`);
    if (params.string !== undefined) entry.socket.send(params.string);
    else entry.socket.send(bytes(params.data ?? ''));
    return {};
  }

  private close(params: WebSocketRequestParams): Record<string, unknown> {
    const entry = this.sockets.get(params.socket ?? 0);
    if (!entry) return {};
    // Browsers accept 1000 and 3000–4999 from a page; other codes close normally.
    const code = params.code === 1000 || (params.code !== undefined && params.code >= 3000 && params.code <= 4999) ? params.code : 1000;
    entry.socket.close(code, params.reason ?? '');
    this.sockets.delete(params.socket ?? 0);
    const closed = { closeCode: code, reason: params.reason ?? '' };
    for (const wake of entry.waiting.splice(0)) wake(closed);
    return {};
  }
}
